import React, { useState, useMemo } from "react";
import { Container, Grid, Stack } from "@mui/material";
import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UserControls from "../components/UserControls";
import ArticleCard from "../components/ArticleCard";
import CompactListCard from "../components/CompactListCard";

const guides = [
    {
        title: "how to use lebronbot",
        description: "everything you need to get started on getting trade data and how to report your own!",
        path: "/guides/how-to-lebron",
        image: require('../assets/cards/lebronbot_card.png'),
        tags: ["bot", "reporting", "guide"]
    },
    {
        title: "beginner's guide to neocash",
        description: "whether you're a new or returning trader, this will cover the basics of neocash!",
        path: "/guides/beginner-nc-guide",
        image: require('../assets/cards/stylisher_card.png'),
        tags: ["beginner", "guide"]
    },
    {
        title: "lebron values userscript",
        description: "the userscript that displays lebron values under item images",
        path: "/userscript",
        image: require('../assets/cards/lebron_userscript_card.png'),
        tags: ["article"]
    },
    {
        title: "how a value guide works",
        description: "let's talk about the importance of reporting!",
        path: "/articles/how-a-value-guide-works",
        image: require('../assets/cards/reporting_card.gif'),
        tags: ["article", "values"]
    },
    {
        title: "updates",
        description: "check out all our updates regarding the stylisher project!",
        path: "/updates",
        image: require('../assets/cards/updates_card.png'),
        tags: ["updates"]
    },
    {
        title: "frequently asked questions",
        description: "covering some common questions that we see or anticipate regarding our project!",
        path: "/faq",
        image: require('../assets/cards/faq_card.png'),
        tags: ["article"]
    },
    {
        title: "how to buy neocash",
        description: "let's talk about which package gives you the most bang for your buck",
        path: "/guides/buying-nc",
        image: require('../assets/cards/nc_card.png'),
        tags: ["beginner", "guide"]
    },
    {
        title: "all about bfgbcs",
        description: "why are bfgbcs so useful yet so weird?",
        path: "/articles/bfgbc",
        image: require('../assets/cards/bfgbc_card.png'),
        tags: ["bfgbc", "article", "values"]
    },
    {
        title: "ways to determine and interpret values",
        description: "values are made up but how do you even make them up?",
        path: "/articles/values",
        image: require('../assets/cards/values_card.png'),
        tags: ["article", "values"]
    },
    {
        title: "token search",
        description: "a general search for token trades by color and/or species",
        path: "/token-search",
        image: require('../assets/cards/token_search_card.png'),
        tags: ["token"]
    },
    {
        title: "token trading guide",
        description: "does token trading seem super chaotic? yeah it was a mess to write this guide too LOL",
        path: "/guides/token-trading",
        image: require('../assets/cards/umbra_card.png'),
        tags: ["token", "guide"]
    },
    {
        title: "miscellaneous nc knowledge",
        description: "why is everyone suddenly online at 10am?",
        path: "/articles/misc-nc-knowledge",
        image: require('../assets/cards/lisha_card.png'),
        tags: ["article"]
    },
    {
        title: "mme",
        description: "why does everyone want MME path B",
        path: "/articles/mme",
        image: require('../assets/cards/mme.png'),
        tags: ["article"]
    },
    {
        title: "nc shuffles",
        description: "the basics on how nc shuffles work!",
        path: "/guides/nc-shuffles",
        image: require('../assets/cards/shuffle_card.png'),
        tags: ["games", "guide"]
    },
    {
        title: "neoboard fonts",
        description: "premade fonts to display your tradelist!",
        path: "/articles/neosignatures",
        image: require('../assets/cards/neoboard_card.png'),
        tags: ["article"]
    },
    {
        title: "hunting down hard to find items",
        description: "have you ever found the perfect item except there's only 10 people who have it uft",
        path: "/guides/hunting-htf-items",
        image: require('../assets/cards/hunting_htf_card.png'),
        tags: ["guide"]
    },
    {
        title: "anniversary nc quiz",
        description: "a fun quiz to celebrate our one year anniversary!",
        path: "/articles/anniversary-nc-quiz",
        image: require('../assets/cards/library_faerie_card.png'),
        tags: ["article", "fun"]
    },
    {
        title: "drop rate simulator",
        description: "if my pulls are unlucky then it's rigged!",
        path: "/articles/drop-rate-simulator",
        image: require('../assets/cards/drop_rate_simulator_card.png'),
        tags: ["article", "fun"]
    }
];

const ResourceHub = () => {
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTags, setSelectedTags] = useState([]);
    const [sortMode, setSortMode] = useState("default");
    const [viewMode, setViewMode] = useState("card");

    const allTags = useMemo(() => Array.from(new Set(guides.flatMap(g => g.tags || []))), []);

    const filteredGuides = useMemo(() => {
        return guides
            .filter(g => g.title.toLowerCase().includes(searchTerm.toLowerCase()))
            .filter(g =>
                selectedTags.length === 0 ||
                g.tags?.some(tag => selectedTags.includes(tag))
            )
            .sort((a, b) => {
                if (sortMode === "asc") return a.title.localeCompare(b.title);
                if (sortMode === "desc") return b.title.localeCompare(a.title);
                return 0;
            });
    }, [searchTerm, selectedTags, sortMode]);

    return (
        <Container maxWidth="md" sx={{ py: 1 }}>
            <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                    color: theme.palette.accent.main,
                    fontWeight: 600,
                    mb: 4
                }}
            >
                Resource Hub
            </Typography>

            <UserControls
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                sortMode={sortMode}
                setSortMode={setSortMode}
                viewMode={viewMode}
                setViewMode={setViewMode}
                allTags={allTags}
            />

            {viewMode === "card" ? (
                <Grid container spacing={4} justifyContent="center">
                    {filteredGuides.map((guide, index) => (
                        <Grid key={index}>
                            <ArticleCard guide={guide} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Stack spacing={2}>
                    {filteredGuides.map((guide, index) => (
                        <CompactListCard guide={guide} key={index} />
                    ))}
                </Stack>
            )}
        </Container>
    );
};

export default ResourceHub;
